import React, { useState, useCallback, useMemo } from 'react';
import { Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import classNames from 'classnames';

import { INQUIRY_STATUS_ID, InquiryNotificationPreference } from '@/lib/models';
import { navigationService } from '@/lib/services';
import { useHandleError, useAccount, useNavigationConfig } from '@/hooks';
import { AsyncPage } from '@/components';
import { inquirySettingService } from '@/lib/services/inquiry-settings-service';
import { Helmet } from 'react-helmet';

export const AdvisorSettings = () => {
	const handleError = useHandleError();
	const { account } = useAccount();
	const { setNavigationConfig } = useNavigationConfig();

	const [inquiryNotificationPreferences, setInquiryNotificationPreferences] = useState<
		InquiryNotificationPreference[]
	>([]);
	const newInquiriesSubmittedPreference = useMemo(
		() => inquiryNotificationPreferences.find((pref) => pref.settingId === INQUIRY_STATUS_ID.OPEN),
		[inquiryNotificationPreferences]
	);
	const allOtherPreferences = useMemo(
		() => inquiryNotificationPreferences.filter((pref) => pref.settingId !== INQUIRY_STATUS_ID.OPEN),
		[inquiryNotificationPreferences]
	);

	const [isLoading, setIsLoading] = useState(false);
	const [show, setShow] = useState(false);

	const fetchData = useCallback(async () => {
		if (!account) {
			throw new Error('Account not found.');
		}

		const response = await inquirySettingService.getPreferences(account.accountId).fetch();
		setInquiryNotificationPreferences(response.inquiryNotificationPreferences);
	}, [account]);

	async function handleChange({ currentTarget }: React.ChangeEvent<HTMLInputElement>) {
		setIsLoading(true);

		try {
			if (!account) {
				throw new Error('Account not found.');
			}

			const preferencesResponse = await inquirySettingService
				.setPreferences(account.accountId, {
					inquiryNotificationPreferences: inquiryNotificationPreferences.map((pref) =>
						pref.settingId === currentTarget.value
							? {
									...pref,
									shouldNotify: currentTarget.checked,
							  }
							: pref
					),
				})
				.fetch();
			const navigationResponse = await navigationService.getNavigationItems().fetch();

			setInquiryNotificationPreferences(preferencesResponse.inquiryNotificationPreferences);
			setNavigationConfig(navigationResponse);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}

	async function handleAllStatusChange({ currentTarget }: React.ChangeEvent<HTMLInputElement>) {
		setIsLoading(true);

		try {
			if (!account) {
				throw new Error('Account not found.');
			}

			if (!newInquiriesSubmittedPreference) {
				throw new Error('newInquiriesSubmittedPreference not found.');
			}

			const preferencesResponse = await inquirySettingService
				.setPreferences(account.accountId, {
					inquiryNotificationPreferences: [
						newInquiriesSubmittedPreference,
						...allOtherPreferences.map((preference) => ({
							...preference,
							shouldNotify: currentTarget.checked,
						})),
					],
				})
				.fetch();

			const navigationResponse = await navigationService.getNavigationItems().fetch();

			setInquiryNotificationPreferences(preferencesResponse.inquiryNotificationPreferences);
			setNavigationConfig(navigationResponse);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Account - Advisor Settings</title>
			</Helmet>
			<Row>
				<Col>
					<Card>
						<Card.Header>
							<Card.Title className="large">Advisor Settings</Card.Title>
						</Card.Header>
						<Card.Body>
							<h3 className="mb-6">Inquiry Notifications</h3>
							<p className="mb-6">Receive emails related to inquiry activity.</p>
							<Form>
								<fieldset disabled={isLoading}>
									{newInquiriesSubmittedPreference && (
										<Form.Check
											className="mb-4"
											type="checkbox"
											name="inquiry-notifications"
											id={`inquiry-notification-${newInquiriesSubmittedPreference.settingId}`}
											label={newInquiriesSubmittedPreference.description}
											value={newInquiriesSubmittedPreference.settingId}
											checked={newInquiriesSubmittedPreference.shouldNotify}
											onChange={handleChange}
										/>
									)}
									<Form.Check
										className="mb-1"
										type="checkbox"
										name="inquiry-status-changes"
										id="inquiry-status-changes"
										label="Inquiry status changes"
										value=""
										checked={allOtherPreferences.every(({ shouldNotify }) => shouldNotify)}
										onChange={handleAllStatusChange}
									/>
									<div className="pl-5">
										<Button
											variant="link"
											className="font-weight-bold"
											onClick={() => {
												setShow((pv) => !pv);
											}}
										>
											Update individual status changes (
											{allOtherPreferences.filter(({ shouldNotify }) => shouldNotify).length}/
											{allOtherPreferences.length})
										</Button>
										<Collapse in={show}>
											<div>
												<div className="pt-4">
													<div className="p-5 bg-gray-200">
														{allOtherPreferences.map(
															(
																{ description, settingId, shouldNotify },
																preferenceIndex
															) => {
																const isLast =
																	allOtherPreferences.length - 1 === preferenceIndex;

																return (
																	<Form.Check
																		key={description}
																		type="checkbox"
																		className={classNames({ 'mb-4': !isLast })}
																		name="inquiry-notifications"
																		id={`inquiry-notifications-${settingId}`}
																		label={`"${description}" status change`}
																		value={settingId}
																		checked={shouldNotify}
																		onChange={handleChange}
																	/>
																);
															}
														)}
													</div>
												</div>
											</div>
										</Collapse>
									</div>
								</fieldset>
							</Form>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</AsyncPage>
	);
};
